import axios from "axios";
import CrashlyticsService from '../services/crashlyticsService';

// axios.defaults.baseURL = "http://localhost:5007";
// axios.defaults.baseURL = "https://ourapplications.irecman.com/ourdash";
axios.defaults.baseURL = "https://enerapplications.irecman.com/hrapp";

// Add timeout configuration
axios.defaults.timeout = 30000; // 30 seconds timeout

// axios.defaults.headers.post["Content-Type"] = "application/json";

// Function to set the Authorization header
const setAuthorization = (token: string) => {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

// Check for existing token in localStorage
const authUser = localStorage.getItem("authUser");
if (authUser) {
  try {
    const token = JSON.parse(authUser);
    if (token) {
      setAuthorization(token);
    }
  } catch (error) {
    // Failed to parse auth token
  }
}

// Axios Response Interceptor
axios.interceptors.response.use(
  response => response.data || response,
  async error => {
    const originalRequest = error.config;

    // Capture API error in crashlytics (skip for auth endpoints to prevent conflicts)
    const skipEndpoints = [
      '/auth/',
      '/login',
      '/api/users/auth',
      '/api/users/auth/login',
      '/api/users/register',
      '/api/users/admin/reset-password',
      '/api/users/admin/send-email-verification'
    ];
    const shouldSkip = skipEndpoints.some(endpoint => originalRequest?.url?.includes(endpoint));
    
    if (!shouldSkip) {
      try {
        const crashlytics = CrashlyticsService.getInstance();
        // Avoid spamming crashlytics with repeated identical API errors within a short window
        crashlytics.captureApiError(
          originalRequest?.url || 'unknown',
          originalRequest?.method || 'unknown',
          error,
          originalRequest?.data
        );
      } catch (crashlyticsError) {
        // Failed to capture error in crashlytics
      }
    }

    // Handle timeout errors
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return Promise.reject('Request timeout. Please try again.');
    }

    // Handle 403 errors (session expired)
    if (
      error.response?.status === 403 &&
      error.response.data?.message === "Session expired or invalid." &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem("authUserRefreshToken");
        let parsedRefreshToken;
        
        try {
          parsedRefreshToken = JSON.parse(refreshToken || 'null');
        } catch (error) {
          parsedRefreshToken = null;
        }

        if (parsedRefreshToken) {
          const refreshResponse = await axios.post(
            "/api/users/auth/refresh",
            {},
            {
              headers: {
                Authorization: `Bearer ${parsedRefreshToken}`
              },
              timeout: 10000 // 10 seconds for refresh token
            }
          );

          const newToken = refreshResponse.data.access_token;
          const newRefreshToken = refreshResponse.data.refresh_token;

          localStorage.setItem("authUser", JSON.stringify(newToken));
          localStorage.setItem("authUserRefreshToken", JSON.stringify(newRefreshToken));
          setAuthorization(newToken);

          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return axios(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem("authUser");
        localStorage.removeItem("authUserRefreshToken");
        localStorage.removeItem("userDetails");
        localStorage.removeItem("loginTimestamp");
        localStorage.removeItem("passwordChangedAt");
        window.location.href = "/login";
        return Promise.reject('Session expired. Please login again.');
      }
    }

    // Account lockout (423): pass object so Login can show countdown and disable form
    if (error.response?.status === 423) {
      const errorData = error.response?.data;
      return Promise.reject({
        isLockout: true,
        message: errorData?.message || "Account temporarily locked due to too many failed login attempts. Please try again later.",
        lockoutSecondsRemaining: errorData?.data?.lockout_seconds_remaining,
      });
    }

    // IP block / breach (429): too many failed logins from same network
    if (error.response?.status === 429) {
      const errorData = error.response?.data;
      return Promise.reject({
        isIpBlocked: true,
        message: errorData?.message || "Too many failed login attempts from your network. Access temporarily blocked. Please try again later.",
        retryAfterSeconds: errorData?.data?.retry_after_seconds,
      });
    }

    // Handle specific error status codes
    const statusMessages: { [key: number]: string } = {
      500: "Internal Server Error",
      401: "Invalid credentials",
      403: "Access forbidden",
      404: "Sorry! The data you are looking for could not be found",
      408: "Request timeout",
      504: "Gateway timeout",
    };

    // Prefer backend-provided message for *any* status code
    // (403s were getting collapsed into a generic "Access forbidden", hiding the real cause)
    const errorData = error.response?.data;
    if (errorData?.message) {
      return Promise.reject(errorData.message);
    } else if (errorData?.error) {
      return Promise.reject(errorData.error);
    } else if (typeof errorData === 'string' && errorData.trim()) {
      return Promise.reject(errorData);
    }

    // For 400 and 500 errors, try to get the actual error message from the response
    if ((error.response?.status === 400 || error.response?.status === 500)) {
      // Check for different error message formats
      const errorData = error.response?.data;
      if (errorData?.message) {
        return Promise.reject(errorData.message);
      } else if (errorData?.error) {
        return Promise.reject(errorData.error);
      } else if (typeof errorData === 'string') {
        return Promise.reject(errorData);
      } else if (errorData) {
        // Try to extract message from nested structure
        return Promise.reject(JSON.stringify(errorData));
      }
    }

    const errorMessage = statusMessages[error.response?.status] || error.message || 'An error occurred';
    return Promise.reject(errorMessage);
  }
);

class APIClient {
  // Instance method to set the Authorization header
  private setAuthHeader() {
    const token = this.getAuthToken();
    if (token) {
      setAuthorization(token);
    }
  }

  // Get token from localStorage
  private getAuthToken() {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      try {
        const token = JSON.parse(authUser);
        return token;
      } catch (error) {
        return null;
      }
    }
    return null;
  }

  private isFormData(data: any): boolean {
    return data instanceof FormData;
  }

  get = (url: string, params: Record<string, any> = {}) => {
    this.setAuthHeader();
    return axios.get(url, { params });
  };

  create = (url: string, data: any) => {
    this.setAuthHeader();
    if (this.isFormData(data)) {
      return axios.post(url, data);
    } else {
      return axios.post(url, data, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    }
  };

  update = (url: string, data: any) => {
    this.setAuthHeader();
    return axios.patch(url, data);
  };

  put = (url: string, data: any) => {
    this.setAuthHeader();
    if (this.isFormData(data)) {
      return axios.put(url, data);
    }
    return axios.put(url, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  delete = (url: string, config: any = {}) => {
    this.setAuthHeader();
    return axios.delete(url, config);
  };
}

const getLoggedUser = () => {
  const authUser = localStorage.getItem("authUser");
  if (authUser) {
    try {
      return JSON.parse(authUser);
    } catch (error) {
      return {};
    }
  }
  return {};
};

export { APIClient, setAuthorization, getLoggedUser };